import { type FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { type Auth, connectAuthEmulator, getAuth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  type Firestore,
  getFirestore,
  initializeFirestore,
} from 'firebase/firestore';

let cachedApp: FirebaseApp | undefined;
let cachedAuth: Auth | undefined;
let cachedDb: Firestore | undefined;

function getApp(): FirebaseApp {
  if (cachedApp) return cachedApp;
  const existing = getApps()[0];
  if (existing) {
    cachedApp = existing;
    return cachedApp;
  }
  cachedApp = initializeApp({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  });
  return cachedApp;
}

const useEmulator =
  import.meta.env.VITE_FIREBASE_USE_EMULATOR === '1' &&
  import.meta.env.VITE_FIREBASE_USE_DEPLOYED !== '1';

export function getAuthInstance(): Auth {
  if (cachedAuth) return cachedAuth;
  cachedAuth = getAuth(getApp());
  if (useEmulator)
    connectAuthEmulator(cachedAuth, 'http://localhost:9099', { disableWarnings: true });
  return cachedAuth;
}

export function getDbInstance(): Firestore {
  if (cachedDb) return cachedDb;
  const app = getApp();
  // Auto-detect long-polling: the streaming WebChannel can fail behind proxies
  // or on QUIC/HTTP3-degraded networks (ERR_QUIC_PROTOCOL_ERROR). This lets the
  // SDK detect a failing stream and fall back to long-polling, which is far more
  // robust. Must run before any getFirestore() for this app, so we init here and
  // fall back to getFirestore if Firestore was already initialized elsewhere.
  try {
    cachedDb = initializeFirestore(app, { experimentalAutoDetectLongPolling: true });
  } catch {
    cachedDb = getFirestore(app);
  }
  if (useEmulator) connectFirestoreEmulator(cachedDb, 'localhost', 8080);
  return cachedDb;
}
